import type { ConnectionPreview, SoftwareDto } from '@bk/contracts';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { api, errorMessage } from '../../lib/api';
import { useTenant } from '../../lib/useTenant';
import { Button, ErrorText, Field, Select } from '../../ui';

/** Perché un programma non si può collegare dal sito (ADR 0011). */
export const WEB_BLOCKED: Record<string, string> = {
  vietato:
    'Le condizioni d’uso del programma vietano l’accesso automatico: il collegamento dal sito non è possibile.',
  due_passaggi:
    'Il programma chiede sempre la verifica in due passaggi: il collegamento dal sito non è possibile.',
  captcha: 'Il programma mostra un captcha all’accesso: il collegamento dal sito non è possibile.',
};

/**
 * Si può proporre di collegare dal sito: sempre, con l'indirizzo indicato dall'utente, tranne quando si sa già che non
 * è possibile (condizioni d'uso, verifica in due passaggi obbligatoria, captcha). La valutazione può non conoscere il
 * sito (programmi poco noti, su misura o con un nome generico): l'utente sa dove entra.
 */
export const webOffered = (s: SoftwareDto) =>
  !!s.exploration ||
  (!s.connection && !s.assessing && !(s.assessment && WEB_BLOCKED[s.assessment.web.status]));

/** Mentre l'esplorazione è in corso, l'elenco dei software si aggiorna da solo. */
function useExplorationPolling(s: SoftwareDto) {
  const { tenant } = useTenant();
  const queryClient = useQueryClient();
  const running = s.exploration?.status === 'in_corso';
  useEffect(() => {
    if (!running) return;
    const timer = setInterval(
      () => void queryClient.invalidateQueries({ queryKey: ['tenant', tenant.id, 'software'] }),
      3000,
    );
    return () => clearInterval(timer);
  }, [running, queryClient, tenant.id]);
}

export function PreviewTable({ preview }: { preview: ConnectionPreview }) {
  return (
    <>
      {preview.resources.map((r) => (
        <div key={r.key} className="text-xs">
          <p className="font-medium">
            {r.label}:{' '}
            {r.error ? (
              <span className="text-red-700">{r.error}</span>
            ) : (
              `${r.rows} righe di esempio`
            )}
          </p>
          {r.sample.length > 0 && (
            <div className="mt-1 overflow-x-auto">
              <table className="text-left">
                <thead>
                  <tr>
                    {Object.keys(r.sample[0]!).map((k) => (
                      <th key={k} className="pr-3 font-medium text-ink/60">
                        {k}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {r.sample.map((row, i) => (
                    <tr key={i}>
                      {Object.values(row).map((v, j) => (
                        <td key={j} className="pr-3">
                          {String(v ?? '')}
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      ))}
    </>
  );
}

/** Avanzamento dell'esplorazione e, quando è pronta, anteprima e conferma del collegamento. */
export function ExplorationPanel({ s, onDone }: { s: SoftwareDto; onDone: () => void }) {
  const { t } = useTenant();
  useExplorationPolling(s);
  const e = s.exploration;
  const connect = useMutation({
    mutationFn: (onlyWorking: boolean) =>
      api<SoftwareDto>(t(`/software/${s.id}/connection/web/connect`), {
        body: onlyWorking ? { only_working: true } : {},
      }),
    onSuccess: onDone,
  });
  const cancel = useMutation({
    mutationFn: () => api<void>(t(`/software/${s.id}/connection/web/cancel`), { method: 'POST' }),
    onSuccess: onDone,
  });
  if (!e) return null;
  const working = e.preview?.resources.filter((r) => !r.error) ?? [];
  return (
    <div className="mt-3 space-y-2 rounded-lg bg-black/[0.03] p-3 text-sm">
      {e.status === 'in_corso' && (
        <p className="text-ink/65">
          Il sistema è entrato in {s.name} e sta facendo la mappa di tutte le sue pagine, poi
          sceglierà cosa leggere. Può volerci fino a mezz’ora: puoi chiudere questa pagina e tornare
          dopo. {e.step ?? '…'}
          {e.explored && ` · ${e.explored.pages} tipi di pagina nella mappa`}
        </p>
      )}
      {e.status === 'non_possibile' && (
        <p className="rounded-lg bg-amber-50 px-3 py-2 text-amber-900">{e.error}</p>
      )}
      {e.status === 'errore' && <p className="text-red-700">{e.error}</p>}
      {e.status === 'pronta' && e.preview && (
        <>
          <p className="text-ink/70">
            {e.kind === 'sessione_sito'
              ? `Il sistema legge ${s.name} con le stesse chiamate che usano le sue pagine.`
              : `Il sistema legge ${s.name} aprendo le sue pagine, come farebbe una persona.`}{' '}
            {e.explored && `Ha visto ${e.explored.pages} pagine.`} Controlla che i dati siano quelli
            giusti.
          </p>
          <PreviewTable preview={e.preview} />
          <div className="flex flex-wrap gap-2">
            {e.preview.ok ? (
              <Button disabled={connect.isPending} onClick={() => connect.mutate(false)}>
                {connect.isPending ? 'Collegamento…' : 'I dati sono giusti: collega'}
              </Button>
            ) : (
              working.length > 0 && (
                <Button disabled={connect.isPending} onClick={() => connect.mutate(true)}>
                  {connect.isPending
                    ? 'Collegamento…'
                    : `Collega solo ${working.map((r) => r.label).join(', ')}`}
                </Button>
              )
            )}
          </div>
        </>
      )}
      {e.status !== 'in_corso' && (
        <button className="text-xs text-ink/50 hover:underline" onClick={() => cancel.mutate()}>
          Chiudi
        </button>
      )}
      <ErrorText>
        {connect.error
          ? errorMessage(connect.error)
          : cancel.error
            ? errorMessage(cancel.error)
            : ''}
      </ErrorText>
    </div>
  );
}

/**
 * Collegamento dal sito: pagina di accesso, utente e password, consenso esplicito. Il sistema entra, esplora e prova;
 * si collega solo dopo aver visto l'anteprima.
 */
export function WebConnectForm({ s, onDone }: { s: SoftwareDto; onDone: () => void }) {
  const { t } = useTenant();
  const [loginUrl, setLoginUrl] = useState(
    s.assessment?.web.login_url && !s.assessment.web.login_url.includes('{{')
      ? s.assessment.web.login_url
      : '',
  );
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [consent, setConsent] = useState(false);
  const [hours, setHours] = useState('6');
  const start = useMutation({
    mutationFn: () =>
      api<SoftwareDto>(t(`/software/${s.id}/connection/web`), {
        body: {
          login_url: loginUrl.trim(),
          username,
          password,
          consent,
          sync_hours: Number(hours),
        },
      }),
    onSuccess: () => {
      setPassword('');
      onDone();
    },
  });
  if (s.exploration) return <ExplorationPanel s={s} onDone={onDone} />;
  return (
    <form
      className="mt-3 space-y-3 rounded-lg bg-black/[0.03] p-3"
      onSubmit={(e) => {
        e.preventDefault();
        start.mutate();
      }}
    >
      <p className="text-xs text-ink/60">
        Se usate {s.name} da un sito con utente e password, il sistema può entrare con le vostre
        credenziali, una volta capire dove sono i dati e poi rileggerli da solo. Si parte in sola
        lettura; ogni scrittura chiede prima la vostra conferma. Le credenziali restano cifrate e
        non passano mai dalla chat né dal motore AI. Se il sito chiede la verifica in due passaggi o
        un captcha, il collegamento non è possibile.
      </p>
      {s.assessment?.web.login_url?.includes('{{') && (
        <p className="text-xs text-ink/55">
          Indirizzo tipico: {s.assessment.web.login_url.replace(/\{\{[a-z_]+\}\}/g, '…')}
        </p>
      )}
      <Field
        label="Indirizzo del sito (la pagina dove inserite utente e password)"
        type="url"
        placeholder="https://…"
        value={loginUrl}
        onChange={(e) => setLoginUrl(e.target.value)}
        required
      />
      <Field
        label="Utente"
        autoComplete="off"
        value={username}
        onChange={(e) => setUsername(e.target.value)}
        required
      />
      <Field
        label="Password"
        type="password"
        autoComplete="new-password"
        value={password}
        onChange={(e) => setPassword(e.target.value)}
        required
      />
      <Select
        label="Ogni quanto rileggere i dati"
        value={hours}
        onChange={(e) => setHours(e.target.value)}
      >
        <option value="1">ogni ora</option>
        <option value="6">ogni 6 ore</option>
        <option value="24">una volta al giorno</option>
      </Select>
      <label className="flex items-start gap-2 text-xs text-ink/75">
        <input
          type="checkbox"
          className="mt-0.5"
          checked={consent}
          onChange={(e) => setConsent(e.target.checked)}
          required
        />
        <span>
          Confermo che le condizioni d’uso di {s.name} consentono l’accesso automatico con il nostro
          account e autorizzo il sistema a entrare con queste credenziali per leggere i dati
          dell’azienda. Conviene un utente dedicato con i soli permessi necessari.
        </span>
      </label>
      <Button type="submit" disabled={start.isPending || !consent}>
        {start.isPending ? 'Avvio…' : 'Entra, esplora e mostra l’anteprima'}
      </Button>
      <ErrorText>{start.error ? errorMessage(start.error) : ''}</ErrorText>
    </form>
  );
}

/** Scritture disponibili nel programma collegato dal sito: dati, anteprima (modulo compilato, non inviato), conferma. */
export function WebWrites({ s }: { s: SoftwareDto }) {
  const { t } = useTenant();
  const writes = s.connection?.writes ?? [];
  const [open, setOpen] = useState<string | null>(null);
  const [values, setValues] = useState<Record<string, string>>({});
  const [preview, setPreview] = useState<{
    token: string;
    screenshot: string;
    filled: Array<{ label: string; value: string }>;
    label: string;
  } | null>(null);
  const [outcome, setOutcome] = useState<{ verified: boolean | null; message: string } | null>(
    null,
  );
  const prepare = useMutation({
    mutationFn: () =>
      api<{
        token: string;
        screenshot: string;
        filled: Array<{ label: string; value: string }>;
        label: string;
      }>(t(`/software/${s.id}/connection/write/preview`), { body: { write: open, values } }),
    onSuccess: (p) => {
      setPreview(p);
      setOutcome(null);
    },
  });
  const confirmWrite = useMutation({
    mutationFn: () =>
      api<{ verified: boolean | null; message: string }>(t(`/software/${s.id}/connection/write`), {
        body: { token: preview!.token },
      }),
    onSuccess: (r) => {
      setOutcome(r);
      setPreview(null);
      setValues({});
    },
  });
  if (!writes.length) return null;
  const w = writes.find((x) => x.key === open);
  return (
    <div className="mt-3 space-y-2 text-sm">
      <p className="text-xs text-ink/55">
        Si può anche scrivere in {s.name}, sempre con anteprima e conferma:
      </p>
      <div className="flex flex-wrap gap-2">
        {writes.map((x) => (
          <Button
            key={x.key}
            variant="ghost"
            onClick={() => {
              setOpen(open === x.key ? null : x.key);
              setPreview(null);
              setOutcome(null);
              setValues({});
            }}
          >
            {x.label}
          </Button>
        ))}
      </div>
      {w && !preview && (
        <form
          className="space-y-2 rounded-lg bg-black/[0.03] p-3"
          onSubmit={(e) => {
            e.preventDefault();
            prepare.mutate();
          }}
        >
          {w.inputs.map((i) => (
            <Field
              key={i.name}
              label={i.label}
              type={i.tipo === 'data' ? 'date' : i.tipo === 'numero' ? 'number' : 'text'}
              step="any"
              value={values[i.name] ?? ''}
              onChange={(e) => setValues({ ...values, [i.name]: e.target.value })}
              required={i.required}
            />
          ))}
          <Button type="submit" variant="ghost" disabled={prepare.isPending}>
            {prepare.isPending ? 'Compilazione del modulo…' : 'Prepara (non invia ancora)'}
          </Button>
        </form>
      )}
      {preview && (
        <div className="space-y-2 rounded-lg border border-amber-300 bg-amber-50 p-3">
          <p className="font-medium text-amber-900">
            Stai per scrivere in {s.name}: «{preview.label}»
          </p>
          <ul className="text-xs">
            {preview.filled.map((f) => (
              <li key={f.label}>
                {f.label}: <b>{f.value || '—'}</b>
              </li>
            ))}
          </ul>
          <img
            src={preview.screenshot}
            alt="Modulo compilato, non ancora inviato"
            className="max-h-80 rounded border border-black/10"
          />
          <div className="flex gap-2">
            <Button disabled={confirmWrite.isPending} onClick={() => confirmWrite.mutate()}>
              {confirmWrite.isPending ? 'Invio e verifica…' : 'Confermo: scrivi'}
            </Button>
            <Button variant="ghost" onClick={() => setPreview(null)}>
              Annulla
            </Button>
          </div>
        </div>
      )}
      {outcome && (
        <p className={outcome.verified === false ? 'text-amber-800' : 'text-emerald-800'}>
          {outcome.message}
        </p>
      )}
      <ErrorText>
        {prepare.error
          ? errorMessage(prepare.error)
          : confirmWrite.error
            ? errorMessage(confirmWrite.error)
            : ''}
      </ErrorText>
    </div>
  );
}
